import { create } from 'zustand';
import { persist } from 'zustand/middleware';

import useAppointmentInformationStore from './appointment-information.store';
import usePatientInformationStore from './patient-information.store';
import useScheduleQuestionsStore from './schedule-questions.store';

type StepData = {
	name: string;
	number: number;
};

const AVAILABLE_STEPS: StepData[] = [
	{ name: 'start_about_you', number: 0 },
	{ name: 'patient_information', number: 1 },
	{ name: 'location_date_time_page_load', number: 2 },
	{ name: 'complete', number: 3 },
];

type StepManagerState = {
	currentStep: number;
	currentStepName: () => string | null;
	isTransitioning: boolean;
	wasOnConfirmation: boolean;
	appointmentDate: string | null;
	stepOnePtc: boolean;
};

type StepManagerActions = {
	setWasOnConfirmation: (value: boolean) => void;
	setAppointmentDate: (date: string | null) => void;
	setStepOnePtc: (stepOnePtc: boolean) => void;
	setSpecifiedStep: (step: number) => void;
	setIsTransitioning: (isTransitioning: boolean) => void;
	clear: () => void;
	restoreStepOnLoad: () => void;
};

const clearAllStores = () => {
	// Helper function to clear all necessary state across stores
	useAppointmentInformationStore.persist.clearStorage();
	useAppointmentInformationStore.getState().clear();

	usePatientInformationStore.persist.clearStorage();
	usePatientInformationStore.getState().clear();

	useScheduleQuestionsStore.persist.clearStorage();
	useScheduleQuestionsStore.getState().clear();
};

const initialState = {
	currentStep: 0,
	isTransitioning: false,
	wasOnConfirmation: false,
	appointmentDate: null,
	stepOnePtc: false,
};

// Helper function to determine if an appointment is in the past
const isAppointmentInPast = (appointmentDate: string): boolean => {
	const now = new Date();
	const appointment = new Date(appointmentDate);
	return appointment < now;
};

const useStepManagerStore = create<StepManagerState & StepManagerActions>()(
	persist(
		(set, get) => ({
			currentStep: 0,
			currentStepName: () => {
				const step = AVAILABLE_STEPS.find((step) => step.number === get().currentStep);
				return step ? step.name : null;
			},
			isTransitioning: false,
			setIsTransitioning: (isTransitioning: boolean) => set({ isTransitioning }),
			wasOnConfirmation: false,
			appointmentDate: null,
			stepOnePtc: false,
			setSpecifiedStep: (step: number) => {
				if (step < 0 || step >= AVAILABLE_STEPS.length) {
					console.error('setSpecifiedStep(): Invalid step number');
					return;
				}
				set({ currentStep: step });
			},
			setWasOnConfirmation: (value: boolean) => set({ wasOnConfirmation: value }),
			setAppointmentDate: (date: string | null) => set({ appointmentDate: date }),
			setStepOnePtc: (stepOnePtc) => set({ stepOnePtc }),

			restoreStepOnLoad: () => {
				const { currentStep, wasOnConfirmation, appointmentDate } = get();

				if (wasOnConfirmation) {
					if (!appointmentDate) {
						set({ currentStep: 0, wasOnConfirmation: false });
						return;
					}

					if (isAppointmentInPast(appointmentDate)) {
						// check if consult time is in the past, to clear all stores and reset state
						clearAllStores();
						set({ currentStep: 0, wasOnConfirmation: false });
						return;
					}

					// check if consult time is in the future, stay on the confirmation step and show state
					set({ currentStep: 3 });
					return;
				}

				// if refresh on step 3, show step 2 to rescore lead
				if (currentStep === 2) {
					set({ currentStep: 1 });
					return;
				}

				// default to current step
				set({ currentStep });
			},

			clear: () => {
				const { wasOnConfirmation } = get();
				if (wasOnConfirmation) {
					clearAllStores();
				}
				set(initialState);
			},
		}),
		{
			name: 'step-manager-storage',
			partialize: (state) => ({
				currentStep: state.currentStep,
				wasOnConfirmation: state.wasOnConfirmation,
				appointmentDate: state.appointmentDate,
			}),
		}
	)
);

export default useStepManagerStore;
